import get from 'lodash/get';
import { AnyAction } from 'redux';
import * as actions from 'store/cost/actions';
import { ProviderDetail } from 'store/findProvider/types';
import { handleCostTypes, sortGuidedSearchTopics, uniqueProviders } from './mappers';
import {
  CostProvider,
  CostTermsAndConditionsObject,
  GuidedSearchTopic,
  PatientCostObject,
  ProcedureCostObject,
  SearchTopic
} from './types';

export interface CostState {
  planBenefits?: PatientCostObject;
  costProviders: CostProvider[];
  costSelectedResult?: SearchTopic;
  costSearchResults: SearchTopic[];
  costGuidedSearchTopics: GuidedSearchTopic[];
  costVisitTypeID: string[];
  hasAcceptedCostTC: boolean;
  costTermsAndConditions?: CostTermsAndConditionsObject;
  costIsFetching: boolean;
  dataLoaded: boolean;
  imageDataLoaded: boolean;
  providerImages?: string[];
  costProviderDataIsFetching: boolean;
  hasAcceptedConnectCareTC: boolean;
  error?: string;
  procedureCosts?: ProcedureCostObject;
  costProviderDetails?: ProviderDetail;
  paginationFetching: boolean;
  totalCountOfProviders: number;
  costEstimatorSearchText: string | null;
}

const defaultState: CostState = {
  planBenefits: undefined,
  costProviders: [],
  costSelectedResult: undefined,
  costSearchResults: [],
  costGuidedSearchTopics: [],
  costVisitTypeID: [''],
  hasAcceptedCostTC: false,
  costTermsAndConditions: undefined,
  costIsFetching: false,
  providerImages: undefined,
  costProviderDataIsFetching: false,
  imageDataLoaded: false,
  hasAcceptedConnectCareTC: false,
  dataLoaded: false,
  error: undefined,
  procedureCosts: undefined,
  costProviderDetails: undefined,
  paginationFetching: false,
  totalCountOfProviders: 0,
  costEstimatorSearchText: null
};

const costReducer = (state: CostState = defaultState, action: AnyAction) => {
  switch (action.type) {
    case actions.SET_COST_ESTIMATOR_SEARCH_TEXT:
      return {
        ...state,
        costEstimatorSearchText: get(action, 'payload', null)
      };
    case actions.GET_SEARCH_RESULTS:
      return {
        ...state,
        costIsFetching: true,
        error: undefined
      };
    case actions.GET_SEARCH_RESULTS_SUCCESS:
      return {
        ...state,
        costSearchResults: action.payload.data.data.hits,
        costIsFetching: false
      };
    case actions.GET_SEARCH_RESULTS_FAIL:
      return {
        ...state,
        costIsFetching: false,
        error: action.error
      };
    case actions.GET_PROVIDER_COST:
      return {
        ...state,
        costIsFetching: true,
        error: undefined
      };
    case actions.GET_PROVIDER_COST_SUCCESS:
      return {
        ...state,
        providerCost: get(action, 'payload.data.data', {}),
        costIsFetching: false
      };
    case actions.GET_PROVIDER_COST_FAIL:
      return {
        ...state,
        costIsFetching: false,
        error: action.error
      };
    case actions.GET_PATIENT_COST:
      return {
        ...state,
        costIsFetching: true,
        error: undefined
      };
    case actions.GET_PATIENT_COST_SUCCESS:
      return {
        ...state,
        patientCost: get(action, 'payload.data.data', {}),
        costIsFetching: false
      };
    case actions.GET_PATIENT_COST_FAIL:
      return {
        ...state,
        costIsFetching: false,
        error: action.error
      };

    case actions.SELECT_SEARCH_RESULT:
      return {
        ...state,
        costSelectedResult: action.payload,
        costIsFetching: false
      };
    case actions.GET_PROVIDERS:
      if (action.payload.pagination) {
        return {
          ...state,
          costProviderDataIsFetching: true,
          error: undefined,
          paginationFetching: true
        };
      }
      return {
        ...state,
        costProviderDataIsFetching: true,
        costProviders: [],
        error: undefined,
        totalCountOfProviders: 0
      };
    case actions.GET_PROVIDERS_SUCCESS:
      return {
        ...state,
        costProviders: uniqueProviders([
          ...state.costProviders,
          ...get(action, 'payload.data.data', [])
        ]).sort(
          (cp1: CostProvider, cp2: CostProvider) =>
            cp1.provider.serviceLocation.distance - cp2.provider.serviceLocation.distance
        ),
        procedureCosts: handleCostTypes(get(action, 'payload.data', undefined)),
        costProviderDataIsFetching: false,
        paginationFetching: false,
        totalCountOfProviders: get(action, 'payload.data.totalCount', 0)
      };

    case actions.GET_PROVIDERS_FAIL:
      return {
        ...state,
        costProviderDataIsFetching: false,
        error: action.error
      };

    case actions.GET_GUIDED_SEARCH:
      return {
        ...state,
        costIsFetching: true,
        error: undefined
      };
    case actions.GET_GUIDED_SEARCH_SUCCESS:
      return {
        ...state,
        costGuidedSearchTopics: sortGuidedSearchTopics(action.payload.data),
        costIsFetching: false
      };
    case actions.GET_GUIDED_SEARCH_FAIL:
      return {
        ...state,
        costIsFetching: false,
        error: action.error
      };
    case actions.UPDATE_SCREEN_ITEMS:
      return {
        ...state,
        costUserPath: action.payload
      };
    case actions.SET_PROVIDER_FOR_BOOKING:
      return {
        ...state,
        costProviderForBooking: action.payload
      };
    case actions.CLEAR_PROVIDER_FOR_BOOKING:
      return {
        ...state,
        costProviderForBooking: null
      };
    case actions.GET_VISIT_TYPE_ID:
      return {
        ...state,
        costIsFetching: true,
        error: undefined
      };
    case actions.GET_VISIT_TYPE_ID_SUCCESS:
      return {
        ...state,
        costVisitTypeID: action.payload.data,
        costIsFetching: false
      };
    case actions.GET_VISIT_TYPE_ID_FAIL:
      return {
        ...state,
        costIsFetching: false,
        error: action.error
      };
    case actions.GET_PLAN_BENEFITS:
      return {
        ...state,
        costIsFetching: true,
        error: undefined
      };
    case actions.GET_PLAN_BENEFITS_SUCCESS:
      return {
        ...state,
        planBenefits: action.payload.data,
        costIsFetching: false
      };
    case actions.GET_PLAN_BENEFITS_FAIL:
      return {
        ...state,
        costIsFetching: false,
        error: action.error
      };
    case actions.SET_ACCEPT_TC:
      return {
        ...state,
        hasAcceptedCostTC: true
      };
    case actions.GET_TERMS_AND_CONDITIONS:
      return {
        ...state,
        costIsFetching: true,
        error: undefined
      };
    case actions.GET_TERMS_AND_CONDITIONS_SUCCESS:
      return {
        ...state,
        costTermsAndConditions: action.payload.data,
        costIsFetching: false
      };
    case actions.GET_TERMS_AND_CONDITIONS_FAIL:
      return {
        ...state,
        costIsFetching: false,
        error: action.error
      };
    case actions.PROVIDER_GET_DETAILS_FOR_COST:
      return {
        ...state,
        costIsFetching: true,
        error: undefined
      };
    case actions.PROVIDER_GET_DETAILS_FOR_COST_SUCCESS:
      return {
        ...state,
        costProviderDetails: action.payload.data,
        costIsFetching: false
      };
    case actions.PROVIDER_GET_DETAILS_FOR_COST_FAIL:
      return {
        ...state,
        costIsFetching: false,
        error: action.error
      };
    default:
      return state;
  }
};

export default costReducer;
